import { useState, useEffect, useCallback } from 'react';

const TOKEN_KEY = 'hits_jwt';
const USER_KEY = 'hits_user';

export interface WebUser {
  user_id: number;
  first_name: string;
  last_name?: string;
  email?: string;
  language_code?: string;
}

export function isTelegramEnv(): boolean {
  return !!window.Telegram?.WebApp?.initData;
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function useAuth() {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [webUser, setWebUser] = useState<WebUser | null>(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = !!token;

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/web-api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Ошибка входа');
        return false;
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      setToken(data.token);
      setWebUser(data.user);
      return true;
    } catch {
      setError('Ошибка сети');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (email: string, password: string, firstName: string) => {
    setLoading(true);
    setError(null);
    try {
      const ref = new URLSearchParams(window.location.search).get('ref') || undefined;
      const res = await fetch('/web-api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, first_name: firstName, ref }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Ошибка регистрации');
        return null;
      }
      // New flow: register returns token directly (no email verification)
      if (data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        setToken(data.token);
        setWebUser(data.user);
        return '__authenticated__';
      }
      return data.message as string;
    } catch {
      setError('Ошибка сети');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/web-api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Ошибка');
        return null;
      }
      return data.message as string;
    } catch {
      setError('Ошибка сети');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const confirmResetPassword = useCallback(async (resetToken: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/web-api/auth/reset-password/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Ошибка');
        return null;
      }
      return data.message as string;
    } catch {
      setError('Ошибка сети');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setWebUser(null);
  }, []);

  const linkTelegram = useCallback(async (code: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/web-api/auth/link-telegram', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Ошибка связки');
        return false;
      }
      // Update token and user to the linked TG account
      if (data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
        setToken(data.token);
      }
      if (data.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        setWebUser(data.user);
      }
      return true;
    } catch {
      setError('Ошибка сети');
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loginTelegram = useCallback(async (code: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/web-api/auth/login-telegram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Код неверный или истёк');
        return false;
      }
      if (data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
        setToken(data.token);
      }
      if (data.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        setWebUser(data.user);
      }
      return true;
    } catch {
      setError('Ошибка сети');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Validate token on mount
  useEffect(() => {
    if (!token) return;
    fetch('/web-api/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      if (!res.ok) {
        logout();
      }
    }).catch(() => {
      // Network error — keep token, try later
    });
  }, [token, logout]);

  return {
    token,
    webUser,
    isAuthenticated,
    loading,
    error,
    setError,
    login,
    register,
    resetPassword,
    confirmResetPassword,
    linkTelegram,
    loginTelegram,
    logout,
  };
}
